import { useState, useEffect, lazy, Suspense } from 'react';
import Home from './site/pages/Home.jsx';
import ReviewPrompt from './components/ReviewPrompt.jsx';
import { useDocumentHead } from './utils/useDocumentHead.js';
import { MARKETING_BY_PATH } from './components/marketing/routes.js';
import { POST_BY_SLUG } from './content/blog/index.js';
import { INTENT_BY_SLUG } from './content/intents/index.js';
import { CORE_HEAD } from './seo/routes.js';
import { eagerFor } from './routeComponents.js';
import { RoutePathContext } from './utils/routePath.js';
import './site/site.css';

// Home (the LCP route) and ReviewPrompt stay eager; every other route is
// code-split so the homepage bundle doesn't carry Stripe, the admin dashboard
// (recharts), the blog, or the legal/marketing pages.
const PricingPage = lazy(() => import('./site/pages/PricingPage.jsx'));
const HowItWorks = lazy(() => import('./site/pages/HowItWorks.jsx'));
const WaitlistPage = lazy(() => import('./site/pages/WaitlistPage.jsx'));
const SignInPage = lazy(() => import('./site/pages/SignInPage.jsx'));
const Privacy = lazy(() => import('./components/Privacy.jsx'));
const Terms = lazy(() => import('./components/Terms.jsx'));
const SuperAdmin = lazy(() => import('./components/SuperAdmin.jsx'));
const InviteAccept = lazy(() => import('./components/InviteAccept.jsx'));
// Opened by chrome.runtime.setUninstallURL when the extension is removed.
const Goodbye = lazy(() => import('./components/Goodbye.jsx'));
// Landing for an invited person (§2.1-E referral loop).
const Refer = lazy(() => import('./components/Refer.jsx'));
// Per-group email preferences, reached from every lifecycle email footer.
const EmailPreferences = lazy(() => import('./components/EmailPreferences.jsx'));
// C3 "How to Say It" library — long-tail intent pages (§2.1-B).
const IntentPage = lazy(() => import('./components/IntentPage.jsx'));
const BlogIndex = lazy(() => import('./site/pages/BlogPages.jsx').then((m) => ({ default: m.BlogIndexPage })));
const BlogPost = lazy(() => import('./site/pages/BlogPages.jsx').then((m) => ({ default: m.BlogPostPage })));
const NotFound = lazy(() => import('./components/NotFound.jsx'));

// Blank, site-colored screen shown while a route chunk loads.
const PageFallback = () => (
  <div style={{ minHeight: '100vh', background: '#fff' }} aria-hidden="true" />
);

/**
 * App.jsx — Simple path-based router (website UI handoff).
 *
 *   /             → Home (the product loop is the demo)
 *   /how-it-works → See it, then see why it's you (three acts)
 *   /waitlist     → Surfaces that aren't live yet (siteConfig.js)
 *   /pricing      → Plans + on-site billing management
 *   /signin       → Sign in (also signs in the installed extension)
 *   /account      → Account card (the window's account sheet, reused)
 *   /app          → retired: lands on /signin or /account, honouring ?next, ?upgrade
 *   /privacy, /terms, /blog, /blog/:slug, /for/*, /vs/*, product pages, /how-to/*
 *   /invite/<id>, /goodbye, /refer/*, /email-preferences, /admin
 *
 * No react-router dependency. Per-route <head> metadata is set via
 * useDocumentHead; core/legal/blog-index copy lives in seo/routes.js so the
 * sitemap and the build-time prerendered HTML derive from the same source.
 */
const ROUTE_HEAD = CORE_HEAD;

const DEFAULT_HEAD = {
  title: 'PersonaDraft — Sound like yourself, everywhere',
  description: 'A behavioral engine that maps your voice, learns it over time, and writes as you — across email, chat, and every social surface. Insert, never send.',
};

function headFor(clean) {
  if (ROUTE_HEAD[clean]) return ROUTE_HEAD[clean];
  const mk = MARKETING_BY_PATH[clean];
  if (mk) return { title: mk.title, description: mk.description, path: clean };
  if (clean.startsWith('/how-to/')) {
    const it = INTENT_BY_SLUG[clean.slice('/how-to/'.length)];
    if (it) return { title: it.title, description: it.description, path: clean };
    return { ...DEFAULT_HEAD, path: clean, noindex: true };
  }
  if (clean.startsWith('/blog/')) {
    const post = POST_BY_SLUG[clean.slice('/blog/'.length)];
    if (post) return { title: `${post.title} | PersonaDraft Blog`, description: post.description, path: clean };
    return { ...DEFAULT_HEAD, path: clean, noindex: true };
  }
  if (clean === '/signin') return { title: 'Sign in — PersonaDraft', description: DEFAULT_HEAD.description, path: clean, noindex: true };
  if (clean === '/account') return { title: 'Account — PersonaDraft', description: DEFAULT_HEAD.description, path: clean, noindex: true };
  // App-shell routes — generic head, kept out of the index (robots also blocks these).
  if (clean === '/app' || clean.startsWith('/app/') || clean === '/admin'
      || clean.startsWith('/invite') || clean === '/goodbye'
      || clean.startsWith('/refer') || clean === '/email-preferences') {
    return { ...DEFAULT_HEAD, path: clean, noindex: true };
  }
  return {
    title: 'Page not found — PersonaDraft',
    description: 'The page you are looking for could not be found.',
    path: clean,
    noindex: true,
  };
}

export default function App({ initialPath }) {
  // initialPath is supplied by the build-time prerender (entry-ssg.jsx), which has
  // no window. In the browser it's omitted and the real location wins.
  const [path, setPath] = useState(initialPath ?? window.location.pathname);

  useEffect(() => {
    const onNav = () => setPath(window.location.pathname);
    window.addEventListener('popstate', onNav);
    return () => window.removeEventListener('popstate', onNav);
  }, []);

  // Old links: /billing → /pricing; /app (the retired web form) → sign-in, keeping
  // the query (?next=, ?upgrade=business, ?signin=1) for the page to act on.
  useEffect(() => {
    if (path === '/billing' || path === '/billing/') {
      window.history.replaceState({}, '', '/pricing');
      setPath('/pricing');
    } else if (path === '/app' || path.startsWith('/app/')) {
      window.history.replaceState({}, '', '/signin' + window.location.search);
      setPath('/signin');
    }
  }, [path]);

  const clean = path.replace(/\/$/, '') || '/';
  useDocumentHead(headFor(clean));

  const marketing = MARKETING_BY_PATH[clean];
  const Ready = eagerFor(clean);

  let page;
  if (clean === '/' || clean === '/welcome') {
    page = <Home />;
  } else if (clean === '/app' || clean.startsWith('/app/') || clean === '/signin') {
    page = <SignInPage />;
  } else if (clean === '/account') {
    page = <SignInPage account />;
  } else if (clean === '/admin') {
    page = <SuperAdmin />;
  } else if (clean === '/how-it-works') {
    const Page = Ready || HowItWorks;
    page = <Page />;
  } else if (clean === '/waitlist') {
    const Page = Ready || WaitlistPage;
    page = <Page />;
  } else if (clean === '/pricing' || clean === '/billing') {
    const Page = Ready || PricingPage;
    page = <Page />;
  } else if (clean === '/privacy') {
    const Page = Ready || Privacy;
    page = <Page />;
  } else if (clean === '/terms') {
    const Page = Ready || Terms;
    page = <Page />;
  } else if (clean === '/blog') {
    const Page = Ready || BlogIndex;
    page = <Page />;
  } else if (clean.startsWith('/blog/')) {
    const Page = Ready || BlogPost;
    page = <Page slug={clean.slice('/blog/'.length)} />;
  } else if (marketing) {
    const Page = Ready || marketing.Component;
    page = <Page />;
  } else if (path.startsWith('/invite/')) {
    const code = path.slice('/invite/'.length).replace(/\/$/, '');
    page = <InviteAccept code={code} />;
  } else if (clean === '/goodbye') {
    page = <Goodbye />;
  } else if (clean === '/email-preferences') {
    page = <EmailPreferences />;
  } else if (clean.startsWith('/how-to/')) {
    const Page = Ready || IntentPage;
    page = <Page slug={clean.slice('/how-to/'.length)} />;
  } else if (path.startsWith('/refer/')) {
    const refCode = path.slice('/refer/'.length).replace(/\/$/, '');
    page = <Refer code={refCode} />;
  } else {
    page = <NotFound />;
  }

  return (
    <RoutePathContext.Provider value={clean}>
      <Suspense fallback={<PageFallback />}>
        {page}
      </Suspense>
      <ReviewPrompt />
    </RoutePathContext.Provider>
  );
}
